// Helper function to get tracking identifiers with error handling and retry
async function getTrackingHeaders() {
  try {
    // First attempt to get attributes
    let attributes = window.ShopifyAnalytics?.lib?.trekkie?.defaultAttributes;

    // If not available, wait a bit and try again (analytics might still be loading)
    if (!attributes) {
      await new Promise(resolve => setTimeout(resolve, 500));
      attributes = window.ShopifyAnalytics?.lib?.trekkie?.defaultAttributes;
    }

    // If still not available, try one more time
    if (!attributes) {
      await new Promise(resolve => setTimeout(resolve, 500));
      attributes = window.ShopifyAnalytics?.lib?.trekkie?.defaultAttributes;
    }

    // Only send the unique token for recommendation tracking
    return {
      'X-Unique-Token': attributes?.uniqToken || '',
      'Content-Type': 'application/json'
    };
  } catch (error) {
    console.warn('Failed to extract tracking attributes:', error);
    // Return minimal headers if tracking data is unavailable
    return {
      'Content-Type': 'application/json'
    };
  }
}

// Generic API wrapper that includes tracking headers
async function apiRequest(url, options = {}) {
  const defaultHeaders = await getTrackingHeaders();

  const config = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers
    }
  };

  return await fetch(url, config);
}

// Toggle function for expanding/collapsing a make's models
function toggleModels(element) {
  const makeColumn = element.closest('.make-column');
  const modelList = makeColumn.querySelector('.model-list');

  if (modelList.style.display === 'none') {
    modelList.style.display = 'block';
    element.setAttribute('data-symbol', '−');
    element.setAttribute('aria-expanded', 'true');
  } else {
    modelList.style.display = 'none';
    element.removeAttribute('data-symbol');
    element.setAttribute('aria-expanded', 'false');
  }
}

// Expand or collapse all makes
function toggleAllModels(expand) {
  const modelLists = document.querySelectorAll('.model-list');
  const toggleButtons = document.querySelectorAll('.toggle-button');

  modelLists.forEach(list => {
    list.style.display = expand ? 'block' : 'none';
  });

  toggleButtons.forEach(button => {
    if (expand) {
      button.setAttribute('data-symbol', '−');
    } else {
      button.removeAttribute('data-symbol');
    }
    button.setAttribute('aria-expanded', expand ? 'true' : 'false');
  });
}

// Filter makes based on search input
function filterMakes(searchTerm) {
  const makeColumns = document.querySelectorAll('.make-column');
  const noResultsMessage = document.getElementById('no-results-message');

  let visibleCount = 0;

  makeColumns.forEach(column => {
    const makeName = column.querySelector('.make-title').textContent.toLowerCase();
    if (searchTerm === '' || makeName.includes(searchTerm.toLowerCase())) {
      column.style.display = 'block';
      visibleCount++;
    } else {
      column.style.display = 'none';
    }
  });

  // Show/hide no results message
  if (noResultsMessage) {
    noResultsMessage.style.display = visibleCount === 0 ? 'block' : 'none';
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const container = document.getElementById('DiggerMakesContainer');
  // The API URL is hardcoded here.
  const diggerApiUrl = 'https://api.hypv.ai/ws/digb/v1/all_makes_models/';

  // Simplified check for the container element.
  if (!container) {
    console.error('Digger Makes: Container element not found.');
    return;
  }

  // Fetch data from the hardcoded API URL
  apiRequest(diggerApiUrl)
    .then(response => {
      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.statusText}`);
      }
      return response.json();
    })
    .then(data => {
      // Clear the "Loading..." message
      container.innerHTML = '';

      if (!data || data.length === 0) {
        container.innerHTML = '<p>No makes found.</p>';
        return;
      }

      // Add search/filter controls
      const controls = document.createElement('div');
      controls.className = 'digger-controls';
      controls.innerHTML = `
        <div class="search-container">
          <input type="text" id="make-search" placeholder="Filter makes" aria-label="Search for an excavator make">
        </div>
        <div class="toggle-all-container">
          <button id="expand-all" aria-label="Expand all makes">Expand All</button>
          <button id="collapse-all" aria-label="Collapse all makes">Collapse All</button>
        </div>
      `;
      container.appendChild(controls);

      // Add event listeners for controls
      const searchInput = document.getElementById('make-search');
      searchInput.addEventListener('input', (e) => {
        filterMakes(e.target.value);
      });

      const expandAllButton = document.getElementById('expand-all');
      expandAllButton.addEventListener('click', () => {
        toggleAllModels(true);
      });

      const collapseAllButton = document.getElementById('collapse-all');
      collapseAllButton.addEventListener('click', () => {
        toggleAllModels(false);
      });

      const grid = document.createElement('div');
      grid.className = 'digger-makes-grid';

      // Add no results message (initially hidden)
      const noResults = document.createElement('p');
      noResults.id = 'no-results-message';
      noResults.textContent = 'No brands found matching your search.';
      noResults.style.display = 'none';
      container.appendChild(noResults);

      // Create HTML for each make and its models
      data.forEach(item => {
        const column = document.createElement('div');
        column.className = 'make-column';

        // Create a container for the header with toggle button
        const headerContainer = document.createElement('div');
        headerContainer.className = 'make-header-container';

        // Toggle button
        const toggleButton = document.createElement('button');
        toggleButton.className = 'toggle-button';
        toggleButton.innerHTML = `
          <img src="https://static.hypv.com/c_digb/icon/circle-plus.svg" class="plus-icon" alt="" width="24" height="24" style="display:block;">
          <img src="https://static.hypv.com/c_digb/icon/circle-minus.svg" class="minus-icon" alt="" width="24" height="24" style="display:none;">
        `;
        toggleButton.setAttribute('aria-expanded', 'false');
        toggleButton.setAttribute('aria-label', `Expand ${item.make} models`);
        toggleButton.addEventListener('click', function() {
          toggleModels(this);
        });

        // Make title (uses 'make_name' field from API)
        const makeTitle = document.createElement('h3');
        makeTitle.className = 'make-title';
        makeTitle.textContent = item.make;

        headerContainer.appendChild(toggleButton);
        headerContainer.appendChild(makeTitle);
        column.appendChild(headerContainer);

        if (item.models && item.models.length > 0) {
          const modelList = document.createElement('ul');
          modelList.className = 'model-list';
          modelList.style.display = 'none'; // Hidden by default

          item.models.forEach(model => {
            const modelItem = document.createElement('li');
            const modelLink = document.createElement('a');
            modelLink.href = model.url;
            modelLink.textContent = model.desc_full;

            // Append the link to the list item first
            modelItem.appendChild(modelLink);

            // Check for count_product and append it after the link
            if (model.count_product && model.count_product > 0) {
              const countText = document.createTextNode(` (${model.count_product})`);
              modelItem.appendChild(countText);
            }

            modelList.appendChild(modelItem);
          });
          column.appendChild(modelList);
        }
        grid.appendChild(column);
      });

      // Add the fully constructed grid to the page
      container.appendChild(grid);
    })
    .catch(error => {
      console.error('Error fetching digger makes:', error);
      container.innerHTML = '<p>Sorry, there was an error loading the makes. Please try again later.</p>';
    });
});
